import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, Mail, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

export default function MySubmissions() {
  const { user, loading: authLoading, loginWithGoogle } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.get("/me/submissions", { withCredentials: true });
      setItems(res.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [user]);

  if (authLoading) return null;

  if (!user) {
    return (
      <section className="pt-32 pb-32 md:pt-40 min-h-[60vh] flex items-center" data-testid="mysubs-signin-cta">
        <div className="max-w-md mx-auto px-6 text-center">
          <FileText size={32} className="mx-auto text-[#0066FF]" strokeWidth={1.5} />
          <h1 className="mt-6 font-display text-3xl font-semibold text-slate-900">My Submissions</h1>
          <p className="mt-3 text-base text-slate-600">
            Sign in with Google to view your contact submissions, offers, and saved domains.
          </p>
          <button onClick={loginWithGoogle} className="mt-8 btn-primary" data-testid="mysubs-google-signin">
            Sign In with Google <ArrowRight size={16} />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="pt-32 pb-24 md:pt-40 min-h-[60vh]" data-testid="mysubs-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <div className="eyebrow mb-3">// Account</div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight text-slate-900">
              My Submissions
            </h1>
            <p className="mt-3 text-base text-slate-600">
              Welcome back, <span className="text-slate-900 font-medium">{user.name || user.email}</span>.
              Here is the history of every inquiry you've submitted.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="btn-ghost text-xs font-mono uppercase tracking-[0.15em]"
            data-testid="mysubs-refresh-btn"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>

        {items.length === 0 && !loading && (
          <div className="tracing-card p-10 text-center">
            <FileText size={24} className="mx-auto text-slate-400" strokeWidth={1.5} />
            <p className="mt-4 text-sm text-slate-500">No submissions yet.</p>
            <Link to="/contact" className="mt-6 btn-primary inline-flex" data-testid="mysubs-empty-cta">
              Send Your First Message <ArrowRight size={14} />
            </Link>
          </div>
        )}

        <div className="space-y-4">
          {items.map((s) => (
            <div key={s.id} className="tracing-card p-6" data-testid={`mysubs-item-${s.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg font-semibold text-slate-900">
                      {s.project_type}
                    </span>
                    <span className="text-[0.6rem] font-mono uppercase tracking-[0.15em] px-2 py-0.5 border border-[#0066FF]/40 text-[#0066FF]">
                      {s.submission_kind}
                    </span>
                    {s.domain && (
                      <span className="text-[0.65rem] font-mono text-[#0066FF]">{s.domain}</span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-slate-500 font-mono uppercase tracking-[0.15em]">
                    {new Date(s.created_at).toLocaleString()}
                  </div>
                </div>
                <a
                  href={`mailto:info@sbtechnologies.net?subject=Re: ${encodeURIComponent(s.project_type)}`}
                  className="btn-ghost text-xs font-mono uppercase tracking-[0.15em] shrink-0"
                >
                  <Mail size={12} /> Follow Up
                </a>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                {s.message}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
